import type { Entity } from '@backstage/catalog-model';
import { fullRef, shortRef } from '../vocabulary/refs';

/**
 * The home page's pure pieces, kept out of the component so they can be
 * asserted directly. Everything here is a function of its arguments — the
 * clock in particular is passed in rather than read, which is the only way the
 * hour boundaries are testable at all.
 */

/**
 * Three-way split on the hour: morning to noon, afternoon to six, evening
 * after. Midnight through five reads as "Good morning", which is the
 * convention every mail client and dashboard already follows — precision about
 * the small hours would draw more attention than the greeting deserves.
 */
export const greeting = (now: Date) => {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

/**
 * The leading word of a display name, for the greeting.
 *
 * Undefined rather than empty when there is nothing usable, so the caller can
 * drop the name from the greeting entirely instead of rendering a dangling
 * comma. An identity with no profile — a service account, or a provider that
 * returns no name — should read "Good morning", not "Good morning, ".
 */
export const firstName = (displayName?: string) => {
  const trimmed = displayName?.trim();
  if (!trimmed) return undefined;
  return trimmed.split(/\s+/)[0];
};

/**
 * Where an entity lives in the app.
 *
 * The catalog's route is `/catalog/:namespace/:kind/:name` with a lowercase
 * kind — the same shape BrowsePage builds by hand. Kept here so
 * the home page's several card strips cannot drift from each other.
 */
export const entityPath = (entity: Entity) =>
  `/catalog/${
    entity.metadata.namespace ?? 'default'
  }/${entity.kind.toLowerCase()}/${entity.metadata.name}`;

/**
 * The entities this identity is responsible for, personally or through a group.
 *
 * `ownershipEntityRefs` from the identity API is always fully qualified, while
 * `spec.owner` in the catalog is whatever shorthand the YAML author used, so
 * both sides go through `fullRef` before they are compared. Comparing the raw
 * strings would quietly return nothing for most real catalogs.
 */
export const ownedBy = (entities: Entity[], ownershipRefs: string[]) => {
  if (ownershipRefs.length === 0) return [];
  const mine = new Set(ownershipRefs.map(r => fullRef(r, 'group')));
  return entities.filter(e => {
    const owner = fullRef(e.spec?.owner, 'group');
    return owner !== '' && mine.has(owner);
  });
};

/**
 * What this identity owns, narrowed to the kinds a strip is a list of.
 *
 * Ownership is not the whole story for "Yours": a Template and a Client carry
 * `spec.owner` just as a Component does, so owning one put the same entity in
 * two strips at once — a Template in both "Yours" and "Start something new".
 * The kind restriction is what makes each strip answer a different question.
 */
export const ownedOfKind = (
  entities: Entity[],
  ownershipRefs: string[],
  kinds: Set<string>,
) => ownedBy(entities, ownershipRefs).filter(e => kinds.has(e.kind));

/**
 * What to call a recorded visit.
 *
 * plugin-home's VisitListener names a visit after the entity for catalog URLs,
 * and otherwise after the first path segment — so a visit to /browse comes back
 * as "browse", which is the one page in this app whose path and name disagree.
 * The rest are here so the strip matches the rail rather than the URL bar.
 */
const VISIT_LABELS: Record<string, string> = {
  '/browse': 'Catalog',
  '/create': 'Create',
  '/docs': 'Docs',
  '/marketplace': 'Marketplace',
  '/search': 'Search',
  '/settings': 'Settings',
  '/notifications': 'Notifications',
};

export const visitLabel = (visit: {
  name: string;
  pathname: string;
  entityRef?: string;
}) => {
  if (visit.entityRef) return shortRef(visit.entityRef);
  // Create has sub-pages people mean different things by, and every one of
  // them used to come back as "Create" — five identical rows in a strip whose
  // point is telling them apart.
  const create = visit.pathname.match(
    /^\/create\/(templates|request)(?:\/(.+))?/,
  );
  if (create) {
    const [, tab, rest] = create;
    if (tab === 'request') {
      return rest?.startsWith('task/') ? 'Create · task' : 'My requests';
    }
    if (!rest) return 'Create';
    // `default/answerrocket-service` → "answerrocket-service"; `new-env` stays.
    return `Create · ${rest.split('/').pop()}`;
  }
  // Match on the leading segment so /docs/default/component/x still reads
  // "Docs" rather than falling through to the document title.
  const root = `/${visit.pathname.split('/')[1] ?? ''}`;
  return VISIT_LABELS[root] ?? visit.name;
};

/**
 * "1 component", "2 components", "1 API".
 *
 * The explicit plural is there for the nouns this portal actually uses — API
 * and APIs are not related by adding an "s" to a lowercase word.
 */
export const countLabel = (
  n: number,
  singular: string,
  plural = `${singular}s`,
) => `${n} ${n === 1 ? singular : plural}`;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * Coarse "how long ago" for the recently-visited strip.
 *
 * Deliberately blunt past a week: the point of the strip is picking up where
 * you left off, and anything older than that is not something you are still in
 * the middle of. A negative age — the visits store and the browser clock
 * disagreeing — is clamped to "just now" rather than rendered as "-3m ago".
 */
export const relativeTime = (timestamp: number, now: number) => {
  const age = now - timestamp;
  if (age < MINUTE) return 'just now';
  if (age < HOUR) return `${Math.floor(age / MINUTE)}m ago`;
  if (age < DAY) return `${Math.floor(age / HOUR)}h ago`;
  if (age < WEEK) return `${Math.floor(age / DAY)}d ago`;
  return 'over a week ago';
};
